import DOMPurify from "dompurify";

/**
 * Strips anything that can run script (script/iframe/object tags, on* event
 * handlers, javascript: URLs) from server-supplied rich text, keeping the
 * formatting markup.
 *
 * Work order long descriptions, instructions and inspection text are rendered
 * through react-quill (quill 1.3.7) and html-react-parser. quill 1.x loads its
 * value by assigning it to innerHTML of a live element, so a stored payload such
 * as <img src=x onerror=...> would execute (CVE-2021-3163; quill has no fixed
 * release). Sanitize before handing HTML to either of them.
 *
 * `target` is kept so links that open in a new window keep doing so instead of
 * navigating the app's own WebView away.
 */
export const sanitizeHtml = <T extends string | null | undefined>(html: T): T => {
    if (!html) {
        return html;
    }
    return DOMPurify.sanitize(html as string, { ADD_ATTR: ["target"] }) as T;
};

/**
 * Text content of an HTML fragment. Parses with DOMParser, whose document is
 * inert: unlike assigning innerHTML on an element, nothing in it runs and no
 * images are fetched.
 */
export const getHtmlPlainText = (html: string): string => {
    if (!html) {
        return "";
    }
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.documentElement.textContent || "";
};
